import { useEffect, useState } from 'react';
import { fetchAllAirtableRecords } from '../services/airtable';
import { normalizeEmail } from '../utils/text';

const USUARIOS_FIELD_IDS = {
  nombre: 'fldN1lRrzE9CITnfj',
  iniciales: 'fldRCFbaH18GGpcsM',
  correo: 'fld4byHxdBUfZLT5x',
  rol: 'fldIJ3T9GwDUU82OH',
  generarReportes: 'fldGcxglcqXkDzg8B',
  permisoFacturas: 'fldmeE9h4BBZDTPjX',
  contrasenasCi: 'fldojlDJWzE48tg2o'
};

const toText = value => {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    const first = value.find(item => item !== undefined && item !== null && String(item).trim());
    return first === undefined ? '' : toText(first);
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return String(value).trim();
};

const toBoolean = value => {
  if (value === true) return true;
  if (value === false || value === undefined || value === null) return false;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'si' || normalized === 'sí';
  }
  if (typeof value === 'number') return value === 1;
  return false;
};

/**
 * Carga directorio de usuarios (ID -> iniciales, correo, rol, permisos) para responsables.
 */
export function useUsuariosDirectory({ enabled, usuariosTableId }) {
  const [userDirectory, setUserDirectory] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (!enabled || !usuariosTableId) {
      setUserDirectory({});
      setLoadingUsers(false);
      return undefined;
    }
    let ignore = false;

    async function loadUsers() {
      try {
        setLoadingUsers(true);
        const records = await fetchAllAirtableRecords({
          tableId: usuariosTableId,
          maxRecords: 500,
          returnFieldsByFieldId: true,
          fields: Object.values(USUARIOS_FIELD_IDS)
        });
        if (ignore) return;
        const dir = {};
        records.forEach(record => {
          const fields = record.fields || {};
          const nombre = toText(fields[USUARIOS_FIELD_IDS.nombre] ?? fields.Nombre);
          const iniciales = toText(fields[USUARIOS_FIELD_IDS.iniciales] ?? fields.Iniciales);
          const correo = normalizeEmail(toText(fields[USUARIOS_FIELD_IDS.correo] ?? fields.Correo));
          const rol = toText(fields[USUARIOS_FIELD_IDS.rol] ?? fields.Rol);
          const permisos = {
            reportes: toBoolean(fields[USUARIOS_FIELD_IDS.generarReportes] ?? fields['Generar Reportes']),
            facturas: toBoolean(fields[USUARIOS_FIELD_IDS.permisoFacturas] ?? fields['Permiso Facturas']),
            enlaces: toBoolean(fields[USUARIOS_FIELD_IDS.contrasenasCi] ?? fields['Contraseñas CI']),
            finiquitos: toBoolean(fields['Permiso Finiquitos'] ?? fields['Finiquitos'] ?? fields['Acceso Finiquitos'])
          };

          dir[record.id] = {
            nombre,
            iniciales,
            correo,
            rol,
            permisos
          };
        });
        setUserDirectory(dir);
      } catch (error) {
        console.error('[Usuarios] Error loading directory', error);
        if (!ignore) {
          setUserDirectory({});
        }
      } finally {
        if (!ignore) {
          setLoadingUsers(false);
        }
      }
    }

    loadUsers();
    return () => {
      ignore = true;
    };
  }, [enabled, usuariosTableId]);

  return { userDirectory, loadingUsers };
}
